import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

/**
 * TooltipArrow Component
 * A reusable tooltip with an arrow that appears on hover
 * @param {string|React.ReactNode} text - The tooltip text to display (can be string or JSX)
 * @param {string} position - Position of tooltip relative to parent: "above" or "below" (default: "above")
 * @param {string} className - Additional classes for the tooltip container
 * @param {string} linkText - Optional text for a clickable link (appears after main text)
 * @param {function} onLinkClick - Optional click handler for the link
 * @param {string} buttonText - Optional text for a button (appears as a block button below content)
 * @param {function} onButtonClick - Optional click handler for the button
 * @param {string} buttonClassName - Optional additional classes for the button
 * @param {boolean} usePortal - If true, renders tooltip via Portal to escape overflow containers
 */
const TooltipArrow = ({
  text,
  position = "above",
  className = "",
  linkText = "",
  onLinkClick = null,
  buttonText = "",
  onButtonClick = null,
  buttonClassName = "",
  usePortal = false,
}) => {
  const isAbove = position === "above";
  const [isVisible, setIsVisible] = useState(false);
  const [portalPosition, setPortalPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const hideTimeoutRef = useRef(null);

  // If there's a button, we need to allow wrapping and set min-width
  const hasButton = buttonText && onButtonClick;
  const whitespaceClass = hasButton ? "whitespace-normal" : "whitespace-nowrap";
  const minWidthClass = hasButton ? "min-w-[200px]" : "";

  // Handlers for showing/hiding with delay (for portal mode)
  const handleMouseEnter = () => {
    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    // Set a delay before hiding to allow moving mouse to tooltip
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 100); // 100ms grace period
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // Update portal position when visible
  useEffect(() => {
    if (usePortal && isVisible && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const tooltipHeight = tooltipRef.current?.offsetHeight || 40;

      setPortalPosition({
        top: isAbove ? rect.top - tooltipHeight - 8 : rect.bottom + 8,
        left: rect.left + rect.width / 2,
      });
    }
  }, [usePortal, isVisible, isAbove]);

  const arrowClasses = isAbove
    ? "absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-100"
    : "absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-100";

  const tooltipContent = (
    <>
      {text}
      {linkText && onLinkClick && (
        <>
          {" "}
          <button
            onClick={onLinkClick}
            className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
          >
            {linkText}
          </button>
        </>
      )}
      {buttonText && onButtonClick && (
        <button
          onClick={onButtonClick}
          className={`block mt-1 w-full px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium transition-colors ${buttonClassName}`}
        >
          {buttonText}
        </button>
      )}
      {/* Arrow pointing to the parent element */}
      <div className={arrowClasses}></div>
    </>
  );

  // Portal-based tooltip (escapes overflow containers)
  if (usePortal) {
    return (
      <>
        {/* Invisible trigger area to capture hover */}
        <div
          ref={triggerRef}
          className="absolute inset-0"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
        {isVisible &&
          createPortal(
            <div
              ref={tooltipRef}
              className={`fixed z-[9999] font-inter shadow-lg shadow-gray-700 text-base px-2 py-1 rounded text-xs text-center ${whitespaceClass} ${minWidthClass} transform -translate-x-1/2 pointer-events-auto bg-gray-100 ${className}`}
              style={{
                top: `${portalPosition.top}px`,
                left: `${portalPosition.left}px`,
              }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {tooltipContent}
            </div>,
            document.body
          )}
      </>
    );
  }

  // Standard absolute-positioned tooltip (original behavior)
  const baseClasses = `absolute z-50 shadow-lg shadow-gray-700 text-base px-2 py-1 rounded text-xs ${whitespaceClass} ${minWidthClass} left-1/2 transform -translate-x-1/2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 delay-200 pointer-events-auto hover:opacity-100 hover:visible bg-gray-100`;

  // Use bottom-full for above (grows upward) and top-full for below (grows downward)
  const positionClasses = isAbove ? "bottom-full mb-2" : "top-full mt-2";

  return (
    <div
      className={`text-center ${baseClasses} ${positionClasses} ${className}`}
    >
      {tooltipContent}
    </div>
  );
};

export default TooltipArrow;
